import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);

    console.log('📧 Sending forgot password request for:', email);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      console.log('📬 Response status:', response.status);
      const data = await response.json();
      console.log('📬 Response data:', data);

      if (!response.ok) {
        throw new Error(data.message || 'Failed to send reset email');
      }

      setSuccess(true);
      console.log('✅ Password reset email sent successfully');
    } catch (err: any) {
      console.error('❌ Forgot password error:', err);
      setError(err.message || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Animated Background Elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '-10%',
          left: '-5%',
          width: '40%',
          height: '40%',
          background: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(60px)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '-15%',
          right: '-10%',
          width: '50%',
          height: '50%',
          background: 'radial-gradient(circle, rgba(255, 255, 255, 0.08) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(80px)',
        }}
      />

      {/* Form Container */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: '480px',
            backgroundColor: '#FFFFFF',
            borderRadius: '24px',
            p: { xs: 4, sm: 6 },
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
          }}
        >
          {/* Back Button */}
          <Button
            component={Link}
            to="/login"
            startIcon={<ArrowBack />}
            sx={{
              mb: 3,
              color: '#667eea',
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': { backgroundColor: 'transparent', opacity: 0.8 },
            }}
          >
            Back to Login
          </Button>

          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#FFFFFF' }}>
                FS
              </Typography>
            </Box>
          </Box>

          {/* Title */}
          <Box sx={{ textAlign: 'center', mb: 4, mt: 3 }}>
            <Typography
              variant="h4"
              sx={{
                fontFamily: "'Playfair Display', serif",
                fontWeight: 700,
                fontSize: '2.5rem',
                mb: 1.5,
                color: '#1a1a1a',
              }}
            >
              Reset Password
            </Typography>
            <Typography sx={{ color: '#666666', fontSize: '0.9375rem' }}>
              Enter your email address and we'll send you a link to reset your password
            </Typography>
          </Box>

          {success ? (
            <Alert
              severity="success"
              sx={{
                backgroundColor: '#F0FDF4',
                border: '1px solid #BBF7D0',
                color: '#166534',
                borderRadius: '12px',
              }}
            >
              <Typography sx={{ fontWeight: 600, mb: 1 }}>Email Sent!</Typography>
              <Typography sx={{ fontSize: '0.875rem' }}>
                If an account exists with that email, a password reset link has been sent. Please
                check your inbox and spam folder.
              </Typography>
            </Alert>
          ) : (
            <form onSubmit={handleSubmit}>
              {/* Email */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    color: '#1a1a1a',
                  }}
                >
                  Email Address
                </Typography>
                <TextField
                  fullWidth
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  disabled={loading}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#F8F9FA',
                      borderRadius: '12px',
                      '& fieldset': { borderColor: 'transparent' },
                      '&:hover fieldset': { borderColor: '#667eea' },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea',
                        borderWidth: '2px',
                      },
                    },
                    '& .MuiOutlinedInput-input': {
                      fontSize: '0.9375rem',
                      padding: '14px 16px',
                    },
                  }}
                />
              </Box>

              {/* Error Alert */}
              {error && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    backgroundColor: '#FEF2F2',
                    border: '1px solid #FECACA',
                    color: '#991B1B',
                    borderRadius: '12px',
                  }}
                >
                  {error}
                </Alert>
              )}

              {/* Send Reset Link Button */}
              <Button
                type="submit"
                fullWidth
                disabled={loading}
                sx={{
                  py: 1.75,
                  background: '#000000',
                  color: '#FFFFFF',
                  fontSize: '0.9375rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '12px',
                  boxShadow: 'none',
                  '&:hover': {
                    background: '#1a1a1a',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  },
                  '&:disabled': {
                    backgroundColor: '#E5E7EB',
                    color: '#9CA3AF',
                  },
                }}
              >
                {loading ? <CircularProgress size={20} sx={{ color: '#9CA3AF' }} /> : 'Send Reset Link'}
              </Button>
            </form>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ForgotPassword;
